/**
 * NC / PackoutNC forecast grid — matches the FDPUpdated mock layout.
 *
 *   ┌─────────────────────────────────┐
 *   │  Title + Flag threshold pill    │
 *   ├─────────────────────────────────┤
 *   │  Table A: % Applied per month   │  (3 rows, narrow, editable)
 *   ├─────────────────────────────────┤
 *   │  Table B: Variance + 3-month    │
 *   │  forecast (full width)          │
 *   │   ┌────┬─────┬─────┬─────┬───┐  │
 *   │   │Agcy│Hist │ NCE │ Var │M1.│  │
 *   │   └────┴─────┴─────┴─────┴───┘  │
 *   └─────────────────────────────────┘
 *
 * Edit semantics:
 *   - "% Applied" rewrites every agency row's forecast_data[mIdx]
 *     in lockstep (matches mock — single editor per month, applies
 *     to all agencies).
 *   - "Annual NCE" edits `entry.eu` per agency row. This is the
 *     operator-driven adjustment that drives variance%.
 *   - Calc-pack toggles (handled by PackoutPackRow / PackSizeChips)
 *     recompute `entry.annual_market` per agency via the
 *     NC_DOD_FACILITY_COTS bucket rule. `entry.eu` is left untouched.
 *
 * Derived values:
 *   - Variance % = (eu - annual_market) / annual_market * 100
 *   - Per-month forecast = (eu / 12) * (1 + pct/100)
 *   - Cell flagged when |variance %| > store.varianceThreshold.
 */

import { observer } from 'mobx-react';
import { Checkbox } from 'primereact/checkbox';
import { InputNumber, InputNumberValueChangeEvent } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';

import { useEditStore } from './editStoreContext';
import styles from './ncForecastGrid.module.scss';


const NCForecastGrid = observer(() => {
  const store = useEditStore();
  const slice = store.activeSlice;
  if (!slice) return null;

  const data = store.workingByNDC.get(slice.ndc) ?? slice.serverData;
  const months = data.data[0]?.forecast_data.map(m => m.forecast_month) ?? [];

  if (data.data.length === 0) {
    return (
      <div className={styles.empty}>
        No agency rows for this NDC. Check that AnnualMarketSales has data
        for this GFC.
      </div>
    );
  }

  // The mock has a single "% Applied" control per month that flows
  // to every agency. Read the percentage from the first agency row's
  // forecast_data — agencies stay in lockstep because every edit goes
  // through setSharedMonthPercentage.
  const sharedPercentages = months.map(
    (_, mIdx) => data.data[0].forecast_data[mIdx]?.forecast_percentage ?? 0
  );

  // Per-row derived values (variance + per-month forecast).
  const rowMetrics = data.data.map(entry => {
    const annualMarket = entry.annual_market;
    const eu = entry.eu;
    const variancePct =
      annualMarket > 0 ? +(((eu - annualMarket) / annualMarket) * 100).toFixed(1) : 0;
    const isFlagged = Math.abs(variancePct) > store.varianceThreshold;
    const monthlyAvg = eu / 12;
    const monthForecasts = entry.forecast_data.map(
      m => Math.round(monthlyAvg + monthlyAvg * (m.forecast_percentage / 100))
    );
    return { annualMarket, eu, variancePct, isFlagged, monthForecasts };
  });

  // Totals row — excludes rows the user has unchecked
  // (is_facility_selected=false) so the bottom line matches what
  // the user has actually opted into.
  const selectedIdxs = data.data
    .map((entry, idx) => (entry.is_facility_selected ? idx : -1))
    .filter(i => i >= 0);
  const totalHist = selectedIdxs.reduce(
    (s, i) => s + rowMetrics[i].annualMarket,
    0
  );
  const totalNCE = selectedIdxs.reduce(
    (s, i) => s + rowMetrics[i].eu,
    0
  );
  const totalVariancePct =
    totalHist > 0 ? +(((totalNCE - totalHist) / totalHist) * 100).toFixed(1) : 0;
  const isTotalFlagged = Math.abs(totalVariancePct) > store.varianceThreshold;
  const monthTotals = months.map((_, mIdx) =>
    selectedIdxs.reduce((s, i) => s + rowMetrics[i].monthForecasts[mIdx], 0)
  );

  return (
    <div className={styles.wrap}>
      {/* Heading — flag threshold pill is rendered above this grid by
          ForecastTabs (right-anchored, below the calc-packs row), so we
          only carry the title here. */}
      <h4 className={styles.heading}>NCE Adjustment &amp; Variance Analysis</h4>

      {/* Table A: % Applied per month (editable, shared across agencies) */}
      <div className={styles.percentTableWrap}>
        <table className={styles.percentTable}>
          <colgroup>
            <col style={{ width: '120px' }} />
            <col style={{ width: '220px' }} />
          </colgroup>
          <thead>
            <tr>
              <th className={styles.headCell}>Month</th>
              <th className={`${styles.headCell} ${styles.alignRight}`}>% Applied</th>
            </tr>
          </thead>
          <tbody>
            {months.map((m, mIdx) => (
              <tr key={m}>
                <td className={`${styles.bodyCell} ${styles.monthLabel}`}>
                  {formatMonthLabel(m)}
                </td>
                <td className={styles.bodyCell}>
                  <div className={styles.pctEditRow}>
                    <InputText
                      className={styles.pctInput}
                      value={String(sharedPercentages[mIdx])}
                      onChange={e => {
                        // Store clamps to [0,100]; we just parse here.
                        const v = parseFloat(e.target.value);
                        store.setSharedMonthPercentage(
                          slice.ndc,
                          mIdx,
                          Number.isFinite(v) ? v : 0
                        );
                      }}
                    />
                    <span className={styles.pctSign}>%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Table B: Variance + 3-month forecast */}
      <div className={styles.varianceTableWrap}>
        <table className={styles.varianceTable}>
          <thead>
            <tr>
              <th colSpan={4} className={styles.headCell} />
              <th
                colSpan={months.length}
                className={`${styles.headCell} ${styles.forecastGroupHead}`}
              >
                3-Month Provisional Forecast
              </th>
            </tr>
            <tr>
              <th className={`${styles.headCell} ${styles.alignLeft}`}>Agency</th>
              <th className={`${styles.headCell} ${styles.alignRight} ${styles.histHead}`}>
                Historical 12M (SUs)
              </th>
              <th className={`${styles.headCell} ${styles.alignRight} ${styles.histHead}`}>
                Annual NCE (SUs)
              </th>
              <th className={`${styles.headCell} ${styles.alignRight} ${styles.histHead}`}>
                Variance
              </th>
              {months.map((m, mIdx) => (
                <th
                  key={m}
                  className={`${styles.headCell} ${styles.alignRight} ${styles.forecastHead}`}
                >
                  {formatMonthLabel(m)}
                  <div className={styles.headSubLabel}>@ {sharedPercentages[mIdx]}%</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.data.map((entry, rIdx) => {
              const m = rowMetrics[rIdx];
              const isSelected = entry.is_facility_selected;
              const varianceClass = m.isFlagged
                ? styles.varianceFlagged
                : m.variancePct > 0
                  ? styles.variancePositive
                  : styles.varianceNegative;
              const agencyClass =
                entry.facility_cot_agency === 'DoD'
                  ? styles.agencyDoD
                  : styles.agencyVA;
              const checkboxId = `is-fac-sel-nc-${slice.ndc}-${rIdx}`;
              return (
                <tr
                  key={`${entry.facility_cot_agency}-${rIdx}`}
                  className={isSelected ? undefined : styles.unselectedRow}
                >
                  <td className={`${styles.bodyCell} ${agencyClass}`}>
                    <div className={styles.agencyCellInner}>
                      <Checkbox
                        inputId={checkboxId}
                        checked={isSelected}
                        onChange={e =>
                          store.setFacilitySelected(
                            slice.ndc,
                            rIdx,
                            !!e.checked
                          )
                        }
                      />
                      <label htmlFor={checkboxId} className={styles.agencyLabel}>
                        {entry.facility_cot_agency}
                      </label>
                    </div>
                  </td>
                  <td className={`${styles.bodyCell} ${styles.alignRight}`}>
                    {m.annualMarket > 0 ? m.annualMarket.toLocaleString() : '–'}
                  </td>
                  <td className={`${styles.bodyCell} ${styles.alignRight} ${styles.nceInputCell}`}>
                    <InputNumber
                      value={m.eu}
                      onValueChange={(e: InputNumberValueChangeEvent) =>
                        store.setAgencyEU(slice.ndc, rIdx, e.value ?? 0)
                      }
                      mode="decimal"
                      useGrouping
                      min={0}
                      inputClassName={styles.nceInput}
                    />
                  </td>
                  <td
                    className={`${styles.bodyCell} ${styles.alignRight} ${varianceClass}`}
                  >
                    {m.variancePct > 0 ? '+' : ''}
                    {m.variancePct}%
                    {m.isFlagged && <span className={styles.flagIcon}> ⚠</span>}
                  </td>
                  {m.monthForecasts.map((val, mIdx) => (
                    <td
                      key={mIdx}
                      className={`${styles.bodyCell} ${styles.alignRight} ${styles.forecastCell}`}
                    >
                      {val > 0 ? val.toLocaleString() : '–'}
                    </td>
                  ))}
                </tr>
              );
            })}
            {/* TOTAL row */}
            <tr className={styles.totalRow}>
              <td className={styles.bodyCell}>TOTAL</td>
              <td className={`${styles.bodyCell} ${styles.alignRight}`}>
                {totalHist.toLocaleString()}
              </td>
              <td className={`${styles.bodyCell} ${styles.alignRight}`}>
                {totalNCE.toLocaleString()}
              </td>
              <td
                className={`${styles.bodyCell} ${styles.alignRight} ${
                  isTotalFlagged
                    ? styles.varianceFlagged
                    : totalVariancePct > 0
                      ? styles.variancePositive
                      : styles.varianceNegative
                }`}
              >
                {totalVariancePct > 0 ? '+' : ''}
                {totalVariancePct}%
                {isTotalFlagged && <span className={styles.flagIcon}> ⚠</span>}
              </td>
              {monthTotals.map((tot, mIdx) => (
                <td
                  key={mIdx}
                  className={`${styles.bodyCell} ${styles.alignRight} ${styles.forecastCell}`}
                >
                  {tot.toLocaleString()}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      <p className={styles.footnote}>
        Cells marked ⚠ exceed the {store.varianceThreshold}% variance threshold against
        historical 12M.
      </p>
    </div>
  );
});

function formatMonthLabel(yyyymmdd: string): string {
  const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(yyyymmdd);
  if (!m) return yyyymmdd;
  const year = m[1].slice(2);
  const monthIdx = Math.max(0, Math.min(11, parseInt(m[2], 10) - 1));
  return `${SHORT_MONTHS[monthIdx]} '${year}`;
}

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default NCForecastGrid;
